import React, { FC, useState } from "react";
import classNames from "classnames";
import { FormProvider, useForm } from "react-hook-form";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";
import { Fade } from "react-awesome-reveal";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation, Trans } from "react-i18next";
import {
  Dropdown,
  Input,
  Checkbox,
  PhoneInput,
} from "@app/components/common/form";
import { Button } from "@app/components/common";
import { ModalState } from "@app/enums/modal";
import { RecaptchaVersion } from "@app/enums/recaptcha";
import { CustomError } from "@app/enums/errors";
import { FinancialReportFormData } from "@app/types/forms";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { useModal } from "@app/hooks";
import { RECAPTCHA_ACTION } from "../../constants";
import { useFinancialReport } from "./useFinancialReport";
import { useValidationSchema } from "./useValidationSchema";

export const ContactForm: FC = () => {
  const { t } = useTranslation("p_financial-report");
  const validationSchema = useValidationSchema(t);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      report: (t("reports", { returnObjects: true }) as string[])[0],
      phoneCode: {
        alphaCode: "CH",
        code: "41",
      },
    },
  });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { onChangeModalState } = useModal();
  const { sendFinancialReportRequest } = useFinancialReport();
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);

  const onSubmit = async (data: FinancialReportFormData) => {
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);

      await sendFinancialReportRequest(data, recaptchaToken, RECAPTCHA_ACTION);
      methods.reset();
      onChangeModalState(ModalState.FINANCIAL_REPORT_SUCCESS);
    } catch (error) {
      if (error.name === CustomError.RECAPTCHA_NOT_ENOUGH_SCORES) {
        setIsRecaptchaError(true);

        return;
      }

      onChangeModalState(ModalState.ERROR);
      methods.reset();
    }
  };

  const onRecaptchaSubmit = async (token: string) => {
    try {
      const formData = methods.getValues() as FinancialReportFormData;

      await sendFinancialReportRequest(
        formData,
        token,
        RECAPTCHA_ACTION,
        RecaptchaVersion.V2,
      );
      onChangeModalState(ModalState.FINANCIAL_REPORT_SUCCESS);
    } catch (_error) {
      onChangeModalState(ModalState.ERROR);
    } finally {
      methods.reset();
      setIsRecaptchaError(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className={classNames(
            "relative space-y-10 [&_a:hover]:text-primary-content",
          )}
        >
          <h2
            className={classNames(
              "text-regular text-primary-content",
              "lg:max-w-[336px] lg:text-medium",
              "xl:text-medium",
            )}
          >
            {t("form_title")}
          </h2>
          <div className="space-y-5">
            <Input name="firstName" label={`${t("field_name")} *`} />
            <Input name="lastName" label={`${t("field_lastName")} *`} />
            <Input name="email" label={`${t("field_email")} *`} />
            <PhoneInput
              codeName="phoneCode"
              phoneName="phone"
              label={`${t("field_phone")} *`}
            />
            <Dropdown
              name="report"
              label={`${t("field_report")} *`}
              options={(t("reports", { returnObjects: true }) as string[]).map(
                (option) => {
                  return { value: option, label: option };
                },
              )}
            />
            <Checkbox
              name="isTermsAccepted"
              label={
                <Trans
                  t={t}
                  i18nKey="field_agree_terms"
                  components={[
                    <a
                      key="privacy-policy"
                      href="/legal/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    />,
                  ]}
                />
              }
            />
          </div>
          <Button
            label={t("form_button")}
            type="submit"
            isLoading={methods.formState.isSubmitting || isRecaptchaError}
          />
          {isRecaptchaError ? (
            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <ReCAPTCHA
                sitekey={process.env.GATSBY_RECAPTCHA_V2_KEY}
                onChange={onRecaptchaSubmit}
              />
            </div>
          ) : null}
        </form>
      </Fade>
    </FormProvider>
  );
};
